/**
 * CommandPalette — ⌘K overlay for fast navigation + actions.
 *
 * Built on cmdk (Paco/Vercel's headless command-menu kit). Mirrors the
 * Linear / Vercel dashboard / Raycast pattern — now table-stakes UX in
 * 2025-2026 B2B SaaS per [cmdk guide](https://knowledge.buka.sh/boost-your-react-app-with-a-sleek-command-palette-using-cmdk/).
 *
 * Commands fall into three groups:
 *   - Navigate: jump to any admin page
 *   - Docs: jump to any /docs page
 *   - Actions: contact support, sign out
 *
 * Keyboard:
 *   - ⌘K / Ctrl+K opens
 *   - ↑↓ navigate, Enter run, Esc close
 *
 * Telemetry: command_palette_opened + command_palette_action events tell
 * us which commands users look for (lost discoverability signals) and
 * which are dead weight.
 *
 * Styling uses existing CSS tokens (var(--bg-card), var(--border),
 * var(--accent-teal)) so the Mac Studio Product System redesign
 * (tokens.css) restyles it naturally when that branch merges.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Command } from 'cmdk';
// Radix Dialog primitives for a11y — cmdk uses Radix Dialog internally
// but doesn't expose Title/Description, so we import them directly
// (via cmdk's transitive dep).
import * as Dialog from '@radix-ui/react-dialog';
import { useAuth } from '../context/AuthContext';
import { buildSupportMailto } from '../utils/supportMailto';
import { DOC_PAGES } from '../pages/docs/docsStyles';
import { trackEvent } from '../services/posthog';

const VISUALLY_HIDDEN = {
  position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};

// Admin navigation commands — mirrors AdminSidebar items plus a few
// detail-page entries. Keep in sync with src/components/AdminSidebar.jsx
// and src/App.jsx routes.
const NAV_COMMANDS = [
  { id: 'dashboard',  label: 'Dashboard',         to: '/admin',              keywords: ['home', 'overview', 'metrics'] },
  { id: 'projects',   label: 'Projects',          to: '/admin/projects',     keywords: ['jobs', 'work'] },
  // Phase 5.75 B-14 — deliverables as a first-class palette destination (UI label "Deliverables"; route + DB stay `dataset`)
  { id: 'datasets',   label: 'Deliverables',      to: '/admin/datasets',     keywords: ['data', 'catalog', 'datasets', 'provenance', 'lineage', 'deliverable'] },
  { id: 'annotators', label: 'Annotators',                          to: '/admin/workforce',                       keywords: ['team', 'people', 'labelers', 'roster'] },
  // Applications is now the second tab inside Annotators (merged 2026-04-26).
  // Keep palette entry for discoverability — search "applications" still jumps you there.
  { id: 'apps',       label: 'Applications (Annotators tab)',       to: '/admin/workforce?tab=applications',      keywords: ['hiring', 'pipeline', 'applicants', 'inbox'] },
  { id: 'clients',    label: 'Clients',           to: '/admin/clients',      keywords: ['customers', 'companies'] },
  { id: 'spls',       label: 'SPLs',              to: '/admin/spl-teams',    keywords: ['managers', 'leads'] },
  { id: 'financials', label: 'Financials',        to: '/admin/financials',   keywords: ['money', 'revenue', 'cost'] },
  { id: 'capacity',   label: 'Capacity',          to: '/admin/capacity',     keywords: ['planning', 'staffing'] },
  { id: 'analytics',  label: 'Analytics',         to: '/admin/analytics',    keywords: ['stats', 'charts'] },
  { id: 'reports',    label: 'Reports',           to: '/admin/reports',      keywords: ['client report', 'export'] },
  { id: 'audit',      label: 'Audit Log',         to: '/admin/audit',        keywords: ['history', 'compliance'] },
  { id: 'settings',   label: 'Settings',          to: '/admin/settings',     keywords: ['config', 'preferences'] },
  { id: 'profile',    label: 'My profile',        to: '/admin/profile',      keywords: ['account', 'personal'] },
];

// Phase 5.75 B-14 — quick filters that navigate to /admin/datasets with
// the matching slice's filter params. URL shapes mirror B-1's built-in
// slice seeds (migration 239) + B-4's useDatasetFilterParams URL schema.
const SLICE_COMMANDS = [
  { id: 'slice-all-active',   label: 'Show All Active datasets',         to: '/admin/datasets?status=Active',                        keywords: ['slice', 'active', 'working', 'in progress'] },
  { id: 'slice-delivered-q',  label: 'Show Delivered This Quarter',      to: '/admin/datasets?status=Delivered&delivered=90',        keywords: ['slice', 'delivered', 'quarter', 'client'] },
  { id: 'slice-at-risk',      label: 'Show At Risk datasets (<= 85%)',   to: '/admin/datasets?qmax=85',                              keywords: ['slice', 'risk', 'quality', 'low'] },
  { id: 'slice-needs-review', label: 'Show Needs Review datasets',       to: '/admin/datasets?needs_review=1',                       keywords: ['slice', 'review', 'compliance', 'unknown', 'license', 'pii', 'consent'] },
  { id: 'slice-archived',     label: 'Show Archived datasets',           to: '/admin/datasets?status=Archived',                      keywords: ['slice', 'archived', 'historical', 'past'] },
];

const ACTION_COMMANDS = [
  { id: 'new-project',   label: 'Create a project',      to: '/admin/create-project', keywords: ['add', 'new job', 'start'] },
];

// Phase 5.75 B-14 — admin-only dataset actions. Filtered at render time
// by the useAuth().isAdmin check.
const ADMIN_DATASET_ACTIONS = [
  { id: 'new-dataset',   label: 'Create a new dataset',  to: '/admin/datasets?new=1', keywords: ['add', 'dataset', 'start', 'create'] },
];

// Phase 6 — admin + SPL ingest actions. Filtered at render time via
// (isAdmin || isSPL). Adds a direct path to the upload flow, plus a
// per-job shortcut that lands on the page with the job pre-selected.
const INGEST_ACTIONS = [
  { id: 'csv-ingest',       label: 'Upload CSV (ingest)',          to: '/admin/ingest', keywords: ['csv', 'import', 'upload', 'bulk', 'ingest', 'data'] },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, effectiveRole, isAdmin, isSPL, orgId, signOut } = useAuth();

  // Hide palette on non-app routes — prospects don't need it and we
  // don't want it eating their Cmd+K reflex when on /login.
  const hidden = !user || /^\/(login|signup|legal|sla|__verify)/.test(location.pathname);

  // ⌘K / Ctrl+K toggle
  useEffect(() => {
    function onKey(e) {
      if (hidden) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => {
          if (!v) trackEvent('command_palette_opened', { path: location.pathname });
          return !v;
        });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hidden, location.pathname]);

  const run = useCallback((kind, id, cb) => {
    trackEvent('command_palette_action', { kind, id });
    cb();
    setOpen(false);
  }, []);

  const supportMailto = buildSupportMailto({
    orgId,
    role: effectiveRole,
    path: location.pathname,
    subject: 'Juuni — question',
  });

  if (hidden) return null;

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      description="Search and run commands"
      shouldFilter
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: open ? 'flex' : 'none',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '14vh 16px 0',
        background: 'rgba(10,10,10,0.6)',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      {/* Radix Dialog requires Title + Description children for screen
          reader a11y. Must be direct children of DialogContent (which is
          what Command.Dialog renders). Hidden visually. */}
      <Dialog.Title style={VISUALLY_HIDDEN}>Command palette</Dialog.Title>
      <Dialog.Description style={VISUALLY_HIDDEN}>
        Search pages, run actions, or jump to docs. Arrow keys navigate, Enter runs, Escape closes.
      </Dialog.Description>
      <div style={{
        width: 560,
        maxWidth: '100%',
        // Use --bg-popover (opaque in all themes) instead of --bg-card —
        // dark/vintage themes set --bg-card to ~3% white, which made the
        // palette panel see-through and overlap visually with the page.
        background: 'var(--bg-popover, #fff)',
        borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        border: '1px solid var(--border, rgba(0,0,0,0.1))',
        overflow: 'hidden',
      }}>
        <Command.Input
          placeholder="Type a command or search…"
          style={{
            width: '100%', padding: '16px 18px', fontSize: 14,
            border: 'none', outline: 'none',
            borderBottom: '1px solid var(--border, rgba(0,0,0,0.08))',
            background: 'transparent',
            color: 'var(--text-primary, #0a0a0a)',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        <Command.List style={{
          maxHeight: 420, overflowY: 'auto', padding: 8,
        }}>
          <Command.Empty style={{
            padding: '20px 12px', fontSize: 13,
            color: 'var(--text-muted, #6b7280)', textAlign: 'center',
          }}>
            No commands match. Try a page name or "docs".
          </Command.Empty>

          <Command.Group heading="Navigate" style={groupStyle}>
            {NAV_COMMANDS.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.label} ${c.keywords.join(' ')}`}
                onSelect={() => run('navigate', c.id, () => navigate(c.to))}
                style={itemStyle}
              >
                <span style={iconStyle}>→</span>
                <span>{c.label}</span>
                <span style={hintStyle}>{c.to}</span>
              </Command.Item>
            ))}
          </Command.Group>

          {/* Phase 5.75 B-14 — dataset slice quick-actions. Navigate to
              /admin/datasets with the slice's filter params. URL schema
              matches migration 239 seeds. */}
          <Command.Group heading="Slices" style={groupStyle}>
            {SLICE_COMMANDS.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.label} ${c.keywords.join(' ')}`}
                onSelect={() => run('slice', c.id, () => navigate(c.to))}
                style={itemStyle}
              >
                <span style={iconStyle}>◇</span>
                <span>{c.label}</span>
              </Command.Item>
            ))}
          </Command.Group>

          <Command.Group heading="Actions" style={groupStyle}>
            {ACTION_COMMANDS.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.label} ${c.keywords.join(' ')}`}
                onSelect={() => run('action', c.id, () => navigate(c.to))}
                style={itemStyle}
              >
                <span style={iconStyle}>+</span>
                <span>{c.label}</span>
              </Command.Item>
            ))}
            {/* Phase 5.75 B-14 — admin-only dataset write actions */}
            {isAdmin && ADMIN_DATASET_ACTIONS.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.label} ${c.keywords.join(' ')}`}
                onSelect={() => run('action', c.id, () => navigate(c.to))}
                style={itemStyle}
              >
                <span style={iconStyle}>+</span>
                <span>{c.label}</span>
              </Command.Item>
            ))}
            {/* Phase 6 — ingest actions (admin + SPL) */}
            {(isAdmin || isSPL) && INGEST_ACTIONS.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.label} ${c.keywords.join(' ')}`}
                onSelect={() => run('action', c.id, () => navigate(c.to))}
                style={itemStyle}
              >
                <span style={iconStyle}>↥</span>
                <span>{c.label}</span>
              </Command.Item>
            ))}
            <Command.Item
              value="contact support email help"
              onSelect={() => run('action', 'support', () => {
                window.location.href = supportMailto;
              })}
              style={itemStyle}
            >
              <span style={iconStyle}>✉</span>
              <span>Email support</span>
            </Command.Item>
            <Command.Item
              value="sign out log out logout signout"
              onSelect={() => run('action', 'sign_out', () => { signOut(); })}
              style={itemStyle}
            >
              <span style={iconStyle}>⎋</span>
              <span>Sign out</span>
            </Command.Item>
          </Command.Group>

          <Command.Group heading="Docs" style={groupStyle}>
            {DOC_PAGES.map((p) => (
              <Command.Item
                key={p.path}
                value={`docs ${p.title} ${(p.keywords || []).join(' ')}`}
                onSelect={() => run('docs', p.path, () => navigate(p.path))}
                style={itemStyle}
              >
                <span style={iconStyle}>?</span>
                <span>{p.title}</span>
                <span style={hintStyle}>{p.path}</span>
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>

        <div style={{
          padding: '8px 14px',
          borderTop: '1px solid var(--border, rgba(0,0,0,0.06))',
          fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--text-muted, #6b7280)',
          display: 'flex', gap: 14, alignItems: 'center',
        }}>
          <span><kbd style={kbdStyle}>↑↓</kbd> navigate</span>
          <span><kbd style={kbdStyle}>↵</kbd> run</span>
          <span><kbd style={kbdStyle}>esc</kbd> close</span>
        </div>
      </div>

      {/* cmdk doesn't ship default CSS — items use [data-selected] attr
          to signal the active (keyboard-navigated) row. Style it here
          so the palette feels native. Also group heading spacing. */}
      <style>{`
        [cmdk-group-heading] {
          padding: 8px 12px 4px;
          font-size: 10px;
          font-weight: 500;
          color: var(--text-muted, #6b7280);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-family: 'JetBrains Mono', monospace;
        }
        [cmdk-item][data-selected="true"] {
          background: var(--bg-stat, rgba(93,202,165,0.08));
        }
        [cmdk-item] {
          transition: background 0.08s ease;
        }
      `}</style>
    </Command.Dialog>
  );
}

const groupStyle = {
  marginBottom: 4,
};

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--text-primary, #0a0a0a)',
  cursor: 'pointer',
  fontFamily: "'Inter', -apple-system, sans-serif",
};

const iconStyle = {
  fontSize: 12,
  color: 'var(--text-muted, #6b7280)',
  width: 14,
  textAlign: 'center',
  flexShrink: 0,
};

const hintStyle = {
  marginLeft: 'auto',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  color: 'var(--text-muted, #6b7280)',
};

const kbdStyle = {
  display: 'inline-block',
  padding: '0 5px',
  minWidth: 16,
  fontSize: 10,
  fontFamily: 'inherit',
  background: 'var(--bg-stat, #F3F0E8)',
  border: '1px solid var(--border, rgba(0,0,0,0.08))',
  borderRadius: 3,
  lineHeight: '16px',
  color: 'var(--text-primary, #0a0a0a)',
};
